import { useEffect, useState } from 'react'
import { ClockIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { useForm } from 'react-hook-form'
import { useEffectOnce } from 'react-use'
import { isValidPhoneNumber } from 'libphonenumber-js'
import PricingTable from './PricingTable'
import Stepper from './Stepper'
import { checkoutUrl, enviro } from '../utils/common'
import { jubileumsvaska } from '../assets/photos'

const soldOutSaturday = false
const soldOutSunday = false
const hideSaturdayCompletely = new Date() >= new Date('2026-10-24T18:00:00+02:00')

const BAG_PRICE = 30

const TIERS = [
	{
		inputName: 'saturday',
		name: 'Lördag',
		price: 180,
		description: '24 oktober, 09.30–18.00',
		soldOut: soldOutSaturday,
		hidden: hideSaturdayCompletely,
	},
	{
		inputName: 'sunday',
		name: 'Söndag',
		price: 180,
		description: '25 oktober, 09.30–18.00',
		soldOut: soldOutSunday,
	},
	{
		inputName: 'weekend',
		name: 'Helgpass',
		price: 280,
		description: 'Båda dagarna, 09.30–18.00',
		mostPopular: true,
		hidden: hideSaturdayCompletely,
	},
]

const WHY_WEEKEND =
	'Det finns mer att se än vad som hinns med på en dag. Med helgpass kan du utforska ' +
	'utställarna, boka behandlingar och lyssna på föredragen i lugn takt. Programmet är olika ' +
	'på lördagen och söndagen, så med helgpass får du allt, och du kan komma tillbaka till det ' +
	'du missade.'

const TicketForm = () => {
	const {
		register,
		handleSubmit,
		setValue,
		setError,
		clearErrors,
		getValues,
		watch,
		formState: { errors },
	} = useForm({
		defaultValues: { saturday: 0, sunday: 0, weekend: 0, bags: 0 },
	})

	const visibleTiers = TIERS.filter((tier) => !tier.hidden)

	const [saturday, sunday, weekend, bags] = watch(['saturday', 'sunday', 'weekend', 'bags'])
	const quantities = { saturday, sunday, weekend }
	const totalTickets = visibleTiers.reduce((sum, tier) => sum + (quantities[tier.inputName] || 0), 0)
	const hasTickets = totalTickets > 0
	const totalPrice =
		visibleTiers.reduce((sum, tier) => sum + (quantities[tier.inputName] || 0) * tier.price, 0) +
		(bags || 0) * BAG_PRICE

	const [submitting, setSubmitting] = useState(false)
	const [bagsRemaining, setBagsRemaining] = useState(null)
	const [bagsLoading, setBagsLoading] = useState(true)

	useEffect(() => {
		const fetchBagInventory = async () => {
			try {
				const response = await fetch(checkoutUrl.replace('/checkout', '/bags'))
				if (response.ok) {
					const data = await response.json()
					setBagsRemaining(data.remaining)
				}
			} catch (error) {
				console.error('Error fetching bag inventory:', error)
			} finally {
				setBagsLoading(false)
			}
		}
		fetchBagInventory()
	}, [])

	// The väska is an add-on: it only makes sense alongside a ticket, and the count must
	// never exceed what is actually left in stock.
	useEffect(() => {
		if (!hasTickets && bags > 0) setValue('bags', 0)
	}, [hasTickets, bags, setValue])

	const showBags = !bagsLoading && bagsRemaining > 0
	const canAddBag = hasTickets && bags < bagsRemaining

	const onSubmit = async (data) => {
		if ((data.saturday || 0) + (data.sunday || 0) + (data.weekend || 0) < 1) {
			setError('tickets', { type: 'custom', message: 'Du måste välja minst en biljett' })
			return
		}
		clearErrors('tickets')
		setSubmitting(true)

		const fbData = window.getFacebookData ? window.getFacebookData() : {}
		const fbc = fbData?.fbc
		const fbp = fbData?.fbp
		const gclid = window?.gclid

		const payload = {
			...data,
			...(fbc && { fbc }),
			...(fbp && { fbp }),
			...(gclid && { gclid }),
		}

		try {
			const response = await fetch(checkoutUrl, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload),
			})
			const jsonResponse = await response.json()

			if (jsonResponse.url) {
				window.location.href = jsonResponse.url
				return
			}
			setError('root.checkout', {
				type: 'custom',
				message: 'Det gick inte att starta betalningen. Försök igen.',
			})
		} catch (error) {
			console.error('Error starting checkout:', error)
			setError('root.checkout', {
				type: 'custom',
				message: 'Det gick inte att starta betalningen. Kontrollera din uppkoppling.',
			})
		}
		setSubmitting(false)
	}

	/*
	 * Browsers restore autofilled values into the DOM after React has already mounted and
	 * seeded the form with its defaults, so the fields can look filled while form state is
	 * still empty. Read the DOM back once, a beat after mount, and re-seed from it.
	 */
	useEffectOnce(() => {
		const refill = () => {
			const read = (id) => document.getElementById(id)
			setValue('name', read('name')?.value || '')
			setValue('email', read('email')?.value || '')
			setValue('tel', read('tel')?.value || '')
			setValue('consent', read('consent')?.checked || false)
			TIERS.forEach(({ inputName }) => {
				const field = read(inputName)
				if (field) setValue(inputName, Number(field.value) || 0)
			})
		}
		const timer = setTimeout(refill, 1000)
		return () => clearTimeout(timer)
	})

	const errorMessages = [
		errors.tickets && 'Du måste välja minst en biljett',
		errors.name && 'Vänligen skriv in ditt namn',
		errors.email && 'Vänligen skriv in en giltig e-postadress',
		errors.tel && 'Vänligen skriv in ett giltigt svenskt nummer',
		errors.consent && 'Vänligen acceptera villkoren för att fortsätta',
		errors.root?.checkout?.message,
	].filter(Boolean)

	return (
		<form method='POST' onSubmit={handleSubmit(onSubmit)} noValidate>
			<input type='hidden' value={enviro} {...register('enviro')} />

			<ul className='hx-tiers' style={{ '--tier-columns': visibleTiers.length }}>
				{visibleTiers.map((tier) => (
					<PricingTable
						key={tier.inputName}
						{...tier}
						register={register}
						setValue={setValue}
						getValues={getValues}
						clearErrors={clearErrors}
					/>
				))}
			</ul>

			{!hideSaturdayCompletely && (
				<div className='hx-panel hx-why'>
					<span className='hx-why__icon'>
						<ClockIcon style={{ width: 22, height: 22 }} aria-hidden='true' />
					</span>
					<p>
						<strong>* Varför vi rekommenderar helgpass:</strong> {WHY_WEEKEND}
					</p>
				</div>
			)}

			{showBags && (
				<div className='hx-panel hx-bag'>
					<div className='hx-bag__art'>
						<img src={jubileumsvaska} alt='Jubileumsväska med Harmoni Expos jubileumsdesign' />
					</div>
					<div>
						<h3>
							Jubileumsväska, 30 år!
							{bagsRemaining < 100 && (
								<span className='hx-bag__remaining'>– endast {bagsRemaining} kvar</span>
							)}
						</h3>
						<p>
							Fira 30 år av Harmoni Expo med vår smidiga ryggsäcksväska med jubileumsdesign
							(Limited Edition). Lämnas ut på plats vid incheckning. Begränsat antal!
						</p>
						{!hasTickets && (
							<p className='hx-bag__hint'>Välj minst en biljett för att lägga till väska.</p>
						)}
					</div>
					<div className='hx-bag__buy'>
						<div className='hx-bag__price'>{BAG_PRICE} kr</div>
						<div className='hx-bag__stepper' data-disabled={!hasTickets}>
							<Stepper
								id='bags'
								label='jubileumsväska'
								disabled={!hasTickets}
								canDecrement={bags > 0}
								canIncrement={canAddBag}
								onDecrement={() => setValue('bags', Math.max(0, (bags || 0) - 1))}
								onIncrement={() => setValue('bags', Math.min(bagsRemaining, (bags || 0) + 1))}
								inputProps={register('bags', {
									min: 0,
									max: bagsRemaining,
									valueAsNumber: true,
								})}
								onInputChange={(event) => {
									const typed = parseInt(event.target.value, 10)
									if (Number.isNaN(typed) || typed < 0) setValue('bags', 0)
									else if (typed > bagsRemaining) setValue('bags', bagsRemaining)
								}}
							/>
						</div>
					</div>
				</div>
			)}

			<div className='hx-checkout'>
				<div className='hx-checkout__total'>
					<div className='hx-checkout__count'>
						{totalTickets} {totalTickets === 1 ? 'biljett' : 'biljetter'} valda
					</div>
					<div className='hx-checkout__sum'>{totalPrice} kr</div>
				</div>

				<div className='hx-fields'>
					<div>
						<label className='hx-label' htmlFor='name'>
							Namn *
						</label>
						<input
							id='name'
							type='text'
							autoComplete='name'
							placeholder='För- och efternamn'
							aria-invalid={errors.name ? 'true' : 'false'}
							className='hx-input'
							{...register('name', { required: true })}
						/>
					</div>
					<div>
						<label className='hx-label' htmlFor='email'>
							E-post *
						</label>
						<input
							id='email'
							type='email'
							autoComplete='email'
							placeholder='din@epost.se'
							aria-invalid={errors.email ? 'true' : 'false'}
							className='hx-input'
							{...register('email', {
								required: true,
								pattern: /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/i,
							})}
						/>
					</div>
					<div className='hx-field--wide'>
						<label className='hx-label' htmlFor='tel'>
							Telefonnummer{' '}
							<span className='hx-label__optional'>(valfritt, för SMS-biljett)</span>
						</label>
						<input
							id='tel'
							type='tel'
							autoComplete='tel'
							placeholder='+46 70 123 45 67'
							aria-invalid={errors.tel ? 'true' : 'false'}
							className='hx-input'
							{...register('tel', {
								validate: (value) => !value || isValidPhoneNumber(value, 'SE'),
							})}
						/>
					</div>
				</div>

				<label className='hx-consent' htmlFor='consent'>
					<input id='consent' type='checkbox' {...register('consent', { required: true })} />
					<span>
						Jag godkänner{' '}
						<a href='/villkor-biljettkop/' target='_blank' rel='noreferrer'>
							köpvillkoren
						</a>{' '}
						och behandlingen av mina personuppgifter.
					</span>
				</label>

				{errorMessages.length > 0 && (
					<div className='hx-errors' role='alert'>
						<ExclamationCircleIcon
							className='hx-errors__icon'
							style={{ width: 24, height: 24 }}
							aria-hidden='true'
						/>
						<ul>
							{errorMessages.map((message) => (
								<li key={message}>{message}</li>
							))}
						</ul>
					</div>
				)}

				<div className='hx-submit'>
					<button type='submit' disabled={submitting} className='hx-btn hx-btn--block'>
						{submitting ? (
							<>
								<svg
									className='hx-spinner'
									viewBox='0 0 24 24'
									fill='none'
									aria-hidden='true'
								>
									<circle
										opacity='0.25'
										cx='12'
										cy='12'
										r='10'
										stroke='currentColor'
										strokeWidth='4'
									/>
									<path
										opacity='0.75'
										fill='currentColor'
										d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
									/>
								</svg>
								Laddar...
							</>
						) : (
							'Till kassan'
						)}
					</button>
				</div>

				<div className='hx-checkout__reassure'>Betalning sker säkert via Stripe.</div>
			</div>
		</form>
	)
}

export default TicketForm
